import { formatarDia } from '@common/i18n/formatosData';
import { Card } from '@ntconsult/aluma-react';
import { format } from 'date-fns';
import { AlertTriangle, Circle, CircleCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useModelosConvite } from '../hooks';
import type { Campanha } from '../types/Campanha';
import type { Idioma } from '../types/Painel';
import type { Progresso } from '../types/Progresso';
import { type Acao, prontaParaAbrir } from './regrasDaFase';

type Estado = 'concluido' | 'pendente' | 'terminou';

/** "De … a …" e a estimativa de recebimento: no item Período e no cabeçalho da execução. */
export function usePeriodo(campanha: Campanha) {
  const { t } = useTranslation('campanhas');
  const periodo = { inicio: formatarDia(campanha.inicio), fim: formatarDia(campanha.fim) };
  const entrega =
    campanha.entregaPrevistaInicio && campanha.entregaPrevistaFim
      ? `${formatarDia(campanha.entregaPrevistaInicio)} – ${formatarDia(campanha.entregaPrevistaFim)}`
      : campanha.janelaEnvio;
  return entrega ? t('descricaoComEntrega', { ...periodo, entrega }) : t('descricao', periodo);
}

function Indicador({ estado }: { estado: Estado }) {
  const { t } = useTranslation('campanhas');
  const rotulo = t(`checklist.${estado}`);
  if (estado === 'concluido')
    return <CircleCheck role="img" aria-label={rotulo} className="size-5 text-success" />;
  if (estado === 'terminou')
    return <AlertTriangle role="img" aria-label={rotulo} className="size-5 text-warning" />;
  return <Circle role="img" aria-label={rotulo} className="size-5 text-foreground-muted" />;
}

/**
 * Preparação (spec 36 R2 a R4): três itens sem ordem, cada um com o estado, o resumo e o botão
 * dele, e o bloco de abrir. O primário é um só e vem do `regrasDaFase` (`botao`).
 */
export function ChecklistPreparacao({
  campanha,
  progresso,
  botao,
}: {
  campanha: Campanha;
  progresso: Progresso;
  /** O botão da ação, `primary` se for o primário da fase (R3). */
  botao: (a: Acao, rotulo?: string) => ReactNode;
}) {
  const { t, i18n } = useTranslation(['campanhas', 'participantes']);
  const modelos = useModelosConvite(campanha.id).data;
  const p = progresso;
  const vencida = p.fase === 'vencida';
  const semLista = p.naCampanha + p.excluidos === 0;

  const presentes = (Object.entries(p.idiomas) as [Idioma, number][]).filter(([, n]) => n >= 1);
  const idiomas = presentes.map(([i, n]) => `${i} ${n}`).join(', ');
  // Sem ninguém, o texto que vale é o do português (spec 36 R2).
  const email = (presentes.length > 0 ? presentes.map(([i]) => i) : (['PT'] as Idioma[]))
    .map((i) => {
      const padrao = modelos?.find((m) => m.idioma === i)?.padrao ?? true;
      return `${i} (${t(padrao ? 'campanhas:abrir.padrao' : 'campanhas:abrir.personalizado')})`;
    })
    .join(', ');
  const inicio = formatarDia(campanha.inicio);
  const periodo = usePeriodo(campanha);
  // O dia de hoje no navegador basta: só escolhe a frase (a fase vem da API).
  const agendada = campanha.inicio > format(new Date(), 'yyyy-MM-dd');

  const itens: {
    chave: 'participantes' | 'texto' | 'periodo';
    estado: Estado;
    resumo: string;
    acao: ReactNode;
  }[] = [
    {
      chave: 'participantes',
      estado: p.naCampanha >= 1 ? 'concluido' : 'pendente',
      resumo:
        p.naCampanha >= 1
          ? t('campanhas:abrir.pessoas', { count: p.naCampanha, idiomas })
          : t('campanhas:checklist.ninguem'),
      // Na vencida não se sincroniza (spec 28 R4).
      acao: vencida ? null : botao(semLista ? 'carregarAxiis' : 'carregarDeNovo'),
    },
    {
      chave: 'texto',
      estado: p.textoRevisado ? 'concluido' : 'pendente',
      resumo: p.textoRevisado ? email : t('campanhas:checklist.naoRevisado'),
      acao: p.textoRevisado
        ? botao('editarTexto', t('campanhas:checklist.editarTexto'))
        : botao('revisarTexto'),
    },
    {
      chave: 'periodo',
      estado: vencida ? 'terminou' : 'concluido',
      resumo: periodo,
      acao: botao(vencida ? 'editarDatas' : 'editar'),
    },
  ];

  const falta: string[] = [];
  if (p.naCampanha === 0) falta.push(t('campanhas:checklist.faltaParticipantes'));
  if (!p.textoRevisado) falta.push(t('campanhas:checklist.faltaTexto'));
  if (vencida) falta.push(t('campanhas:checklist.faltaPeriodo'));

  return (
    <div className="flex flex-col gap-8">
      <section aria-label={t('campanhas:checklist.rotulo')} className="grid gap-4 sm:grid-cols-3">
        {itens.map((i) => (
          // `Card.Header`/`Card.Footer` trazem borda e padding próprios: aqui, `h3` e gap.
          <Card
            key={i.chave}
            padding="sm"
            className="flex flex-col items-start gap-4"
            data-item={i.chave}
            data-estado={i.estado}
          >
            <div className="flex min-w-0 items-start gap-3">
              <Indicador estado={i.estado} />
              <div className="flex min-w-0 flex-col gap-1">
                <h3 className="font-semibold text-foreground">
                  {t(`campanhas:checklist.${i.chave}`)}
                </h3>
                <p className="break-words text-sm text-foreground-muted">{i.resumo}</p>
              </div>
            </div>
            {i.acao}
          </Card>
        ))}
      </section>

      <section className="flex flex-col items-start gap-4" data-bloco-abrir>
        <h2 className="text-lg font-semibold text-foreground">
          {t('campanhas:checklist.abrirTitulo')}
        </h2>
        <p className="text-sm text-foreground">
          {agendada
            ? t('campanhas:abrir.oQueAconteceAgendada', { inicio })
            : t('campanhas:abrir.oQueAcontece')}
        </p>
        {prontaParaAbrir(p) ? (
          botao('abrirCampanha')
        ) : (
          <p className="text-sm text-foreground-muted" data-falta>
            {t('campanhas:checklist.falta', {
              itens: new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(falta),
            })}
          </p>
        )}
      </section>
    </div>
  );
}
